import React from "react";

interface LoginFormProps {
  email: string;
  setEmail: (val: string) => void;
  error: string;
  setError: (val: string) => void;
  message: string;
  setMessage: (val: string) => void;
  isAnyLoading: boolean;
  loadingAction: "password" | "link" | "otp" | "invite" | null;
  isValidatingEmail: boolean;
  emailDetails: any;
  hasClickedContinue: boolean;
  selectedRole: "admin" | "client";
  setSelectedRole: (role: "admin" | "client") => void;
  handleContinue: (e?: React.MouseEvent) => void;
  handlePasswordLogin: (e?: React.MouseEvent) => void;
  handleSendMagicLink: (e?: React.MouseEvent) => void;
  handleSendOtpCode: (e?: React.MouseEvent) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  email,
  setEmail,
  error,
  setError,
  message,
  setMessage,
  isAnyLoading,
  loadingAction,
  isValidatingEmail,
  emailDetails,
  hasClickedContinue,
  selectedRole,
  setSelectedRole,
  handleContinue,
  handlePasswordLogin,
  handleSendMagicLink,
  handleSendOtpCode,
}) => {
  const isDecidingWhichButtons = email.length > 0 && isValidatingEmail;
  const showButtons = !isDecidingWhichButtons && !!emailDetails && hasClickedContinue;

  return (
    <div className="w-full max-w-sm">
      <h2 className="text-2xl font-semibold text-[#1f2937] mb-6 text-left font-['Proxima_Nova']">Log in</h2>
      <div className="relative">
        <input
          id="email-input"
          name="email"
          type="email"
          autoComplete="off"
          placeholder=" "
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError("");
            setMessage("");
          }}
          disabled={isAnyLoading}
          className={`peer w-full min-h-[52px] rounded-lg border bg-white px-4 py-0 text-base text-[#1f2937] outline-none focus:ring-0 focus:shadow-none focus:border-2 ${
            error ? "border-red-500" : "border-gray-300"
          }`}
        />
        <label
          htmlFor="email-input"
          className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 bg-white px-1 text-base font-normal transition-all duration-200 ${
            error ? "text-red-500" : "text-[#9CA3AF]"
          } peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-sm peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:-translate-y-1/2 peer-[:not(:placeholder-shown)]:text-sm`}
        >
          Email Address*
        </label>
      </div>
      {error && (
        <span className="mt-0.5 block text-xs font-medium text-red-500">
          {error}
        </span>
      )}
      {message && (
        <span className="mt-0.5 block text-xs font-medium text-green-600">
          {message}
        </span>
      )}

      {!showButtons && (
        <button
          onClick={handleContinue}
          disabled={isAnyLoading || isValidatingEmail}
          className="bg-[#4D5874] relative mt-4 flex w-full items-center justify-center rounded-[10px] h-[52px] min-h-[52px] px-4 text-base font-medium text-white transition-all duration-200 hover:bg-[#4D5874]/90 shadow-md disabled:opacity-70 font-['Proxima_Nova']"
        >
          <span className={isValidatingEmail ? "opacity-0" : ""}>
            Continue
          </span>
          {isValidatingEmail && (
            <span className="absolute h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          )}
        </button>
      )}

      {isDecidingWhichButtons ? null : showButtons ? (
        <>
          {emailDetails?.isMasterUser && emailDetails?.existsInZoho && (
            <div className="flex gap-2 p-1 bg-gray-100 rounded-[10px] mt-4 border border-gray-200">
              <button
                onClick={() => setSelectedRole("client")}
                className={`flex-1 py-2 text-[14px] rounded-[8px] transition-all font-medium ${
                  selectedRole === "client"
                    ? "bg-[#4D5874] text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Client
              </button>
              <button
                onClick={() => setSelectedRole("admin")}
                className={`flex-1 py-2 text-[14px] rounded-[8px] transition-all font-medium ${
                  selectedRole === "admin"
                    ? "bg-[#4D5874] text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Master User
              </button>
            </div>
          )}

          <button
            onClick={handlePasswordLogin}
            disabled={isAnyLoading || isValidatingEmail || !emailDetails}
            className="bg-[#4D5874] relative mt-4 flex w-full items-center justify-center rounded-[10px] h-[52px] min-h-[52px] px-4 text-base font-medium text-white transition-all duration-200 hover:bg-[#4D5874]/90 shadow-md disabled:opacity-70 font-['Proxima_Nova']"
          >
            <span className={loadingAction === "password" ? "opacity-0" : ""}>
              Continue with Password
            </span>
            {loadingAction === "password" && (
              <span className="absolute h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            )}
          </button>

          {(!emailDetails?.isMasterUser || (emailDetails?.isMasterUser && emailDetails?.existsInZoho && selectedRole === "client")) && (
            <>
              <div className="flex items-center gap-3 py-4">
                <span className="flex-1 h-px bg-gray-200" />
                <span className="text-gray-400 text-[11px] uppercase tracking-[0.12em]">
                  or
                </span>
                <span className="flex-1 h-px bg-gray-200" />
              </div>

              <button
                onClick={handleSendMagicLink}
                disabled={isAnyLoading || isValidatingEmail || !emailDetails}
                className="bg-[#4D5874] relative flex w-full items-center justify-center rounded-[10px] h-[52px] min-h-[52px] px-4 text-base font-medium text-white transition-all duration-200 hover:bg-[#4D5874]/90 shadow-md disabled:opacity-70 font-['Proxima_Nova'] mb-4"
              >
                <span className={loadingAction === "link" ? "opacity-0" : ""}>
                  Email me a Magic Link
                </span>
                {loadingAction === "link" && (
                  <span className="absolute h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                )}
              </button>

              <button
                onClick={handleSendOtpCode}
                disabled={isAnyLoading || isValidatingEmail || !emailDetails}
                className="bg-[#4D5874] relative flex w-full items-center justify-center rounded-[10px] h-[52px] min-h-[52px] px-4 text-base font-medium text-white transition-all duration-200 hover:bg-[#4D5874]/90 shadow-md disabled:opacity-70 font-['Proxima_Nova']"
              >
                <span className={loadingAction === "otp" ? "opacity-0" : ""}>
                  Email me a Code
                </span>
                {loadingAction === "otp" && (
                  <span className="absolute h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                )}
              </button>
            </>
          )}
        </>
      ) : null}
    </div>
  );
};

export default LoginForm;